import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { readinessActions as A } from '../../store/readinessSlice';
import LearnPopover from './LearnPopover';
import {
  ASK_LANE_PLACEHOLDER,
  OWEN_RESUME_CHIP_LABEL,
  SIDE_PENDING_HINT,
} from '../../config/readinessQuestions';

/**
 * The composer card: answer chips / currency input / multi-field form
 * section, question hints, the always-present "ask Owen anything" lane,
 * and "← Back a question".
 *
 * While a side answer is pending ("Does that help?…"), the only chip is
 * "I'm all set — let's continue" until the user resumes.
 */
export default function Composer({ q, activeSection, sectionCount, controller }) {
  const dispatch = useDispatch();
  const {
    qi, si, input, ask, sidePending, form, answers, msgs,
  } = useSelector((s) => s.readiness);

  const isRefi = answers.purpose === 'refinance';
  const canGoBack = msgs.some((m) => m.kind === 'user' && m.qid);
  const questionHint = q ? ((isRefi && q.hintRefi) || q.hint || '') : '';

  const showChips = sidePending || (q !== null && q.type === 'chips');
  const showCurrency = q !== null && !sidePending && q.type === 'currency';
  const showForm = activeSection !== null && !sidePending;
  const chipsHint = sidePending ? SIDE_PENDING_HINT : questionHint;
  // Hint + back row belong to assessment questions only (never form sections)
  const showChipsHint = q !== null && showChips && !!chipsHint;
  const showCurrencyHint = showCurrency && !!questionHint;
  const showFooterBack = q !== null && showChips && canGoBack;

  // Owen explanation popover for form fields: { key, rect, title, body }.
  const [explainPop, setExplainPop] = useState(null);
  useEffect(() => { setExplainPop(null); }, [si]);

  const toggleExplain = (f) => (e) => {
    if (explainPop && explainPop.key === f.k) {
      setExplainPop(null);
      return;
    }
    setExplainPop({ key: f.k, rect: e.currentTarget.getBoundingClientRect(), ...f.explain });
  };

  // Focus the currency input each time a new currency question opens.
  const inputRef = useRef(null);
  const lastFocusQi = useRef(null);
  useEffect(() => {
    if (showCurrency && inputRef.current && lastFocusQi.current !== qi) {
      lastFocusQi.current = qi;
      inputRef.current.focus();
    }
  }, [showCurrency, qi]);

  const chips = sidePending
    ? [{ l: OWEN_RESUME_CHIP_LABEL, badge: '', pick: () => controller.resume() }]
    : q && q.type === 'chips'
      ? q.options.map((o) => ({
          l: o.l,
          badge: o.badge || '',
          pick: () => controller.answer(q, o.v, o.l),
        }))
      : [];

  return (
    <div className="rdz-composer">
      {showChips && (
        <div className="rdz-chips">
          {chips.map((opt) => (
            <button key={opt.l} type="button" className="rdz-chip" onClick={opt.pick}>
              <span>{opt.l}</span>
              {opt.badge && <span className="rdz-chip__badge">{opt.badge}</span>}
            </button>
          ))}
        </div>
      )}

      {showCurrency && (
        <div className="rdz-currency">
          <div className="rdz-currency__left">
            <div className="rdz-currency__field">
              <span className="rdz-currency__prefix">$</span>
              <input
                ref={inputRef}
                className="rdz-currency__input"
                value={input}
                onChange={(e) => dispatch(A.setInput(e.target.value))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && input !== '') controller.submitCurrency();
                }}
                placeholder={q.ph || ''}
                inputMode="numeric"
              />
            </div>
            {showCurrencyHint && <div className="rdz-currency__hint">{questionHint}</div>}
          </div>
          <div className="rdz-currency__right">
            <button
              type="button"
              className={'rdz-btn-primary' + (input === '' ? ' rdz-dim' : '')}
              disabled={input === ''}
              onClick={() => controller.submitCurrency()}
            >
              Continue
            </button>
            {canGoBack && (
              <button type="button" className="rdz-btn-back" onClick={() => controller.goBack()}>
                &#8592; Back a question
              </button>
            )}
          </div>
        </div>
      )}

      {showForm && (
        <div className="rdz-form">
          <div className="rdz-form__head">
            <div className="rdz-form__title-row">
              <div className="rdz-form__title">{activeSection.title}</div>
              {activeSection.why && (
                <button
                  type="button"
                  className={'rdz-field__explain-btn rdz-learn-anchor' + (explainPop && explainPop.key === '__section' ? ' rdz-field__explain-btn--open' : '')}
                  onClick={toggleExplain({ k: '__section', explain: activeSection.why })}
                  aria-expanded={explainPop ? explainPop.key === '__section' : false}
                  aria-label={`Owen explains: ${activeSection.title}`}
                  title="Click Owen to learn why this section matters"
                >
                  <img src="/assets/owen-avatar.png" alt="" />
                </button>
              )}
            </div>
            <div className="rdz-form__step">
              Section {si + 1} of {sectionCount} &middot; all fields optional
            </div>
          </div>
          <div className="rdz-form__grid">
            {activeSection.fields.map((f) => (
              <div className="rdz-field" key={f.k}>
                <div className="rdz-field__label-row">
                  <label className="rdz-field__label">{f.label}</label>
                  {f.explain && (
                    <button
                      type="button"
                      className={'rdz-field__explain-btn rdz-learn-anchor' + (explainPop && explainPop.key === f.k ? ' rdz-field__explain-btn--open' : '')}
                      onClick={toggleExplain(f)}
                      aria-expanded={explainPop ? explainPop.key === f.k : false}
                      aria-label={`Owen explains: ${f.label}`}
                      title={`Click Owen to learn more`}
                    >
                      <img src="/assets/owen-avatar.png" alt="" />
                    </button>
                  )}
                </div>
                {f.type === 'note' && (
                  <div className="rdz-field__note">{f.note}</div>
                )}
                {f.type === 'text' && (
                  <input
                    className="rdz-field__input"
                    value={form[f.k] || ''}
                    onChange={(e) => dispatch(A.setFormField({ key: f.k, value: e.target.value }))}
                    placeholder={f.ph || ''}
                  />
                )}
                {f.type === 'currency' && (
                  <div className="rdz-field__currency">
                    <span className="rdz-field__currency-prefix">$</span>
                    <input
                      className="rdz-field__currency-input"
                      value={form[f.k] || ''}
                      onChange={(e) => dispatch(A.setFormField({ key: f.k, value: e.target.value, isCurrency: true }))}
                      placeholder={f.ph || ''}
                      inputMode="numeric"
                    />
                  </div>
                )}
                {f.type === 'select' && (
                  <select
                    className="rdz-field__select"
                    value={form[f.k] || ''}
                    onChange={(e) => dispatch(A.setFormField({ key: f.k, value: e.target.value }))}
                  >
                    {f.options.map((o) => (
                      <option key={o.v} value={o.v}>{o.l}</option>
                    ))}
                  </select>
                )}
              </div>
            ))}
          </div>
          <div className="rdz-form__footer">
            <button type="button" className="rdz-btn-skip" onClick={() => controller.saveSection(true)}>
              Skip for now
            </button>
            <button type="button" className="rdz-btn-save" onClick={() => controller.saveSection(false)}>
              Save &amp; continue
            </button>
          </div>
          {explainPop && (
            <LearnPopover
              anchorRect={explainPop.rect}
              title={explainPop.title}
              body={explainPop.body}
              onClose={() => setExplainPop(null)}
            />
          )}
        </div>
      )}

      {showChipsHint && <div className="rdz-composer__hint">{chipsHint}</div>}

      <div className="rdz-ask-lane">
        <input
          className="rdz-ask-lane__input"
          value={ask}
          onChange={(e) => dispatch(A.setAsk(e.target.value))}
          onKeyDown={(e) => {
            if (e.key === 'Enter') controller.askOwen();
          }}
          placeholder={ASK_LANE_PLACEHOLDER}
        />
        <button
          type="button"
          className={'rdz-ask-lane__btn' + (ask.trim() === '' ? ' rdz-dim' : '')}
          onClick={() => controller.askOwen()}
        >
          Ask
        </button>
      </div>

      {showFooterBack && (
        <div className="rdz-composer__footer-row">
          <button type="button" className="rdz-btn-back" onClick={() => controller.goBack()}>
            &#8592; Back a question
          </button>
        </div>
      )}
    </div>
  );
}
